import { motion } from 'framer-motion';
import {
  Calendar,
  CreditCard,
  CheckCircle,
  X,
  Clock
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { toPersianNumber } from '@/utils/persianNumber';
import type { Transaction } from '../hooks/usePayments';

interface TransactionListProps {
  transactions: Transaction[];
  className?: string;
}

const TransactionList = ({ transactions, className }: TransactionListProps) => {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fa-IR').format(price);
  };

  const getStatusIcon = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <X className="w-5 h-5 text-red-500" />;
      case 'cancelled':
        return <X className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return 'تکمیل شده';
      case 'pending':
        return 'در انتظار';
      case 'failed':
        return 'ناموفق';
      case 'cancelled':
        return 'لغو شده';
    }
  };

  if (transactions.length === 0) {
    return (
      <div className={cn("bg-white rounded-xl shadow-sm p-8 text-center", className)}>
        <div className="text-gray-400 mb-4">
          <CreditCard className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          هیچ تراکنشی یافت نشد
        </h3>
        <p className="text-gray-600">
          تاریخچه پرداخت‌های شما در اینجا نمایش داده خواهد شد
        </p>
      </div>
    );
  }

  return (
    <div className={cn("bg-white rounded-xl shadow-sm overflow-hidden", className)}>
      {/* Transaction List */}
      <div className="divide-y divide-gray-200">
        {transactions.map((transaction, index) => (
          <motion.div
            key={transaction.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors group"
          >
            <div className="flex flex-col gap-3">
              {/* Top Row: Icon + Title + Status + Amount */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    transaction.status === 'completed' ? "bg-green-100 text-green-600" :
                      transaction.status === 'pending' ? "bg-yellow-100 text-yellow-600" :
                        "bg-red-100 text-red-600"
                  )}>
                    {getStatusIcon(transaction.status)}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm font-persian">{transaction.planName ? toPersianNumber(transaction.planName) : 'پرداخت'}</h4>
                    <p className="text-xs text-gray-500 font-persian mt-0.5">{toPersianNumber(transaction.date)}</p>
                  </div>
                </div>

                <div className="text-left">
                  <div className="font-bold text-gray-900 text-sm font-persian">
                    {formatPrice(transaction.amount)} <span className="text-[10px] text-gray-500 font-normal">{transaction.currency}</span>
                  </div>
                  <div className={cn(
                    "text-[10px] font-medium font-persian text-right mt-1",
                    transaction.status === 'completed' ? "text-green-600" :
                      transaction.status === 'pending' ? "text-yellow-600" :
                        "text-red-600"
                  )}>
                    {getStatusText(transaction.status)}
                  </div>
                </div>
              </div>

              {/* Bottom Row: Details (Collapsed/Minimal) */}
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {transaction.orderId && (
                  <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1.5 rounded-lg border border-gray-100">
                    <span className="text-gray-400"><CreditCard className="w-3.5 h-3.5" /></span>
                    <span className="text-[10px] text-gray-600 font-medium font-persian">شماره سفارش:</span>
                    <span className="text-[10px] text-gray-700 font-mono tracking-wider">{toPersianNumber(transaction.orderId)}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default TransactionList;
